<script setup>
import { Link } from '@inertiajs/vue3';
import { computed } from 'vue';

const props = defineProps({
    class: String,
    href: String,
    useInertia: {
        type: Boolean,
        default: true
    },
    target: {
        type: String,
        default: '_self'
    },
    color: {
        type: String,
        default: 'primary',
    },
    animated: {
        type: Boolean,
        default: false
    },
    fontWeight: {
        type: String,
        default: 'normal'
    },
    // Add icon props for social media links
    icon: {
        type: String,
        default: ''
    },
    iconType: {
        type: String,
        default: 'fab' // Default to brands (fab) for social media icons
    },
    // Add new prop to disable underlining
    noUnderline: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['click'])

const LinkClasses = computed(() => {
    const colorClasses = {
        'primary': 'text-brightorange hover:text-lessbrightorange',
        'secondary': 'text-royalblue hover:text-lessroyalblue',
        'tertiary': 'text-gray-500 hover:text-gray-600',
        'white': 'text-white hover:text-gray-50',
        'black': 'text-gray-900 hover:text-gray-900',
    };
    
    return `${props.class || ''} ${colorClasses[props.color] || colorClasses.primary}`;
});

const fontWeightClass = computed(() => {
    const weightClasses = {
        'normal': '',
        'medium': 'font-medium',
        'semibold': 'font-semibold',
        'bold': 'font-bold'
    };

    return weightClasses[props.fontWeight] || '';
});

const baseClasses = computed(() => {
    if (props.animated) {
        return `group transition-all duration-300 ease-in-out inline-flex items-center border-b-2 border-transparent pt-1 ${fontWeightClass.value}`;
    }
    return `duration-200 ${fontWeightClass.value}`;
});

const animationColors = computed(() => {
    // Base color classes without underline styling
    const baseColorClasses = {
        'primary': 'from-brightorange to-brightorange',
        'secondary': 'from-royalblue to-royalblue',
        'tertiary': 'from-gray-500 to-gray-500',
        'white': 'from-white to-white',
        'black': 'from-gray-900 to-gray-900',
    };
    
    // If noUnderline is true, only return the base colors
    if (props.noUnderline) {
        return baseColorClasses[props.color] || baseColorClasses.primary;
    }
    
    // TailwindCSS sm: prefix applies styles for screens LARGER than sm breakpoint
    // which contradicts our requirements. Let's fix the underline classes to behave correctly:
    const fullColorClasses = {
        'primary': 'from-brightorange to-brightorange',
        'secondary': 'from-royalblue to-royalblue',
        'tertiary': 'from-gray-500 to-gray-500',
        'white': 'from-white to-white',
        'black': 'from-gray-900 to-gray-900',
    };
    
    return fullColorClasses[props.color] || fullColorClasses.primary;
});
</script>

<template>
    <Link :href="href" :target="target" :class="[LinkClasses, baseClasses]" v-if="useInertia" @click="$emit('click')">
        <template v-if="animated">
            <span :class="`bg-left-bottom bg-gradient-to-r ${animationColors} bg-[length:0%_2px] bg-no-repeat group-hover:bg-[length:100%_2px] transition-all duration-500 ease-out inline-flex items-center`">
                <font-awesome-icon v-if="icon" :icon="[iconType, icon]" class="mr-2 h-4 w-4" aria-hidden="true" />
                <slot></slot>
            </span>
        </template>
        <template v-else>
            <span class="inline-flex items-center">
                <font-awesome-icon v-if="icon" :icon="[iconType, icon]" class="mr-2 h-4 w-4" aria-hidden="true" />
                <slot></slot>
            </span>
        </template>
    </Link>

    <a :href="href" :target="target" :class="[LinkClasses, baseClasses]" v-else @click="$emit('click')">
        <template v-if="animated">
            <span :class="`bg-left-bottom bg-gradient-to-r ${animationColors} bg-[length:0%_2px] bg-no-repeat group-hover:bg-[length:100%_2px] transition-all duration-500 ease-out inline-flex items-center`">
                <font-awesome-icon v-if="icon" :icon="[iconType, icon]" class="mr-2 h-4 w-4" aria-hidden="true" />
                <slot></slot>
            </span>
        </template>
        <template v-else>
            <span class="inline-flex items-center">
                <font-awesome-icon v-if="icon" :icon="[iconType, icon]" class="mr-2 h-4 w-4" aria-hidden="true" />
                <slot></slot>
            </span>
        </template>
    </a>
</template>